import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import 'react-native-url-polyfill/auto'
import {
  CallEndedEventTypeEnum,
  CallRequestAcceptedEventTypeEnum,
  CallRequestedEventTypeEnum,
  type CallEndedEvent,
  type CallRequestedEvent,
  type CallRequestAcceptedEvent,
  type CallRequestDeclinedEvent,
  CallRequestDeclinedEventTypeEnum,
} from '@dialogue/toktok'
import DeviceInfo from 'APP/Helpers/DeviceInfo'

import { logger } from 'APP/Lib/Datadog'
import Toktok, {
  type CallEndedListener,
  type CallRequestAcceptedListener,
  type CallRequestDeclinedListener,
  type CallRequestedListener,
} from 'APP/Services/Toktok'
import { videoSessionActions } from 'APP/Store/VideoSession'
import { selectIsAppOnline, selectIsAppActive } from 'APP/Sagas/AppSessionSagas'
import { isAccessTokenValid } from 'APP/Lib/AuthHelpers'

const VideoCallSSEListener = (_props: Record<string, unknown>) => {
  const { accessToken } = useSelector((state) => state.login)
  const lastEventId = useRef<string>(null)

  const dispatch = useDispatch()
  const appIsActive = useSelector(selectIsAppActive)
  const appIsOnline = useSelector(selectIsAppOnline)

  const dispatchOnDemandCallHandledByRemote = useCallback(
    (deviceId: string, onDemandSessionId: string) => {
      const currentDeviceId = DeviceInfo.getUniqueId()
      if (deviceId !== currentDeviceId) {
        dispatch(videoSessionActions.onDemandCallHandledByRemote({ onDemandSessionId }))
      }
    },
    [dispatch]
  )

  const callRequestAcceptedHandler = useCallback<CallRequestAcceptedListener>(
    (event) => {
      const {
        payload: { device_id: deviceId, session_id: onDemandSessionId, user_id: userId },
        timestamp,
      } = JSON.parse(event.data as string) as CallRequestAcceptedEvent

      logger.info('[SSE] Received CALL_REQUEST_ACCEPTED event', {
        deviceId,
        eventId: event.lastEventId,
        eventTimestamp: timestamp,
        eventType: event.type,
        onDemandSessionId,
        userId,
      })

      lastEventId.current = event.lastEventId

      dispatchOnDemandCallHandledByRemote(deviceId, onDemandSessionId)
    },
    [dispatchOnDemandCallHandledByRemote]
  )

  const callRequestDeclinedHandler = useCallback<CallRequestDeclinedListener>(
    (event) => {
      const {
        payload: { device_id: deviceId, session_id: onDemandSessionId, user_id: userId },
        timestamp,
      } = JSON.parse(event.data as string) as CallRequestDeclinedEvent

      logger.info('[SSE] Received CALL_REQUEST_DECLINED event', {
        deviceId,
        eventId: event.lastEventId,
        eventTimestamp: timestamp,
        eventType: event.type,
        onDemandSessionId,
        userId,
      })

      lastEventId.current = event.lastEventId
      dispatchOnDemandCallHandledByRemote(deviceId, onDemandSessionId)
    },
    [dispatchOnDemandCallHandledByRemote]
  )

  const callRequestedHandler = useCallback<CallRequestedListener>(
    (event) => {
      const {
        payload: { caller_id: callerId, episode_id: episodeId, session_id: sessionId },
        timestamp,
      } = JSON.parse(event.data as string) as CallRequestedEvent

      logger.info('[SSE] Received CALL_REQUESTED event', {
        callerId,
        episodeId,
        eventId: event.lastEventId,
        eventTimestamp: timestamp,
        eventType: event.type,
        onDemandSessionId: sessionId,
      })

      lastEventId.current = event.lastEventId

      dispatch(videoSessionActions.onDemandCallRequested({ callerId, episodeId, sessionId }))
    },
    [dispatch]
  )

  const callEndedHandler = useCallback<CallEndedListener>(
    (event) => {
      const {
        payload: { session_id: sessionId },
        timestamp,
      } = JSON.parse(event.data as string) as CallEndedEvent

      logger.info('[SSE] Received CALL_ENDED event', {
        eventId: event.lastEventId,
        eventTimestamp: timestamp,
        eventType: event.type,
        onDemandSessionId: sessionId,
      })
      lastEventId.current = event.lastEventId

      dispatch(videoSessionActions.onDemandCallEnded({ onDemandSessionId: sessionId }))
    },
    [dispatch]
  )

  useEffect(() => {
    if (!appIsOnline) {
      logger.warn('[SSE] App may not be online', { appIsOnline })
    }

    const es = Toktok.createEventSource(accessToken, lastEventId.current)
    if (!es) {
      logger.info('[SSE] Event source not initialized, skipping event listeners registration')
      return
    }

    logger.info('[SSE] Configuring event source', {
      lastEventId: lastEventId.current,
      appIsActive,
      appIsOnline,
    })

    es.addEventListener(
      CallRequestAcceptedEventTypeEnum.CALL_REQUEST_ACCEPTED,
      callRequestAcceptedHandler
    )
    es.addEventListener(
      CallRequestDeclinedEventTypeEnum.CALL_REQUEST_DECLINED,
      callRequestDeclinedHandler
    )
    es.addEventListener(CallRequestedEventTypeEnum.CALL_REQUESTED, callRequestedHandler)
    es.addEventListener(CallEndedEventTypeEnum.CALL_ENDED, callEndedHandler)

    es.addEventListener('open', () => {
      logger.info('[SSE] Open connection')
    })

    es.addEventListener('message', (event) => {
      logger.info('[SSE] Received unknown message type', {
        eventType: event.type,
        eventId: event.lastEventId,
      })
      lastEventId.current = event.lastEventId
    })

    es.addEventListener('error', (event) => {
      if (event.type === 'error') {
        logger.info(`[SSE] Connection error: ${event.message}`, {
          detailed_message: event.message,
          xhr_state: event.xhrState,
          xhr_status: event.xhrStatus,
          appIsActive,
        })
      } else if (event.type === 'exception') {
        logger.info(`[SSE] Error: ${event.message}`, {
          detailed_message: event.message,
          error: event.error,
          appIsActive,
        })
      }

      if (!isAccessTokenValid(accessToken)) {
        logger.info('[SSE] Access token has become invalid. Closing connection.')
        es.close()
      }
    })

    es.addEventListener('close', () => {
      logger.info('[SSE] Close connection')
    })

    return () => {
      es.close()
      es.removeAllEventListeners()
    }
  }, [
    accessToken,
    appIsActive,
    appIsOnline,
    callRequestAcceptedHandler,
    callRequestDeclinedHandler,
    callRequestedHandler,
    callEndedHandler,
  ])

  return null
}

export default VideoCallSSEListener
